import {languages, LanguageInfo, youtubeLanguages} from "@/lib/LanguageUtils";

export type TranslationProcess =
    "cloning-dubbing" | "braiv-dubbing-plus" | "elevenlabs-dubbing" | "standard-dubbing" | "translate-captions-only";

export type RoutedDubbingType =
    "cloning-dubbing" | "braiv-dubbing-plus" | "standard-dubbing";

export function normalizeLanguageCodeForRouting(code: unknown): string {
    return typeof code === "string" ? code.trim().replace(/_/g, "-").toLowerCase() : "";
}

const BASE_SOURCE_LANGUAGE_CODES = new Set(
    languages
        .filter((language) => !language.code.includes("-"))
        .map((language) => normalizeLanguageCodeForRouting(language.code)),
);

/**
 * Collapse regional BCP-47 codes (e.g. es-419, en-GB) to the base language code
 * used in import/source-language dropdowns (e.g. es, en).
 */
export function toBaseLanguageCode(code: unknown): string {
    const normalized = normalizeLanguageCodeForRouting(code);
    if (!normalized || normalized === "auto") {
        return normalized || "";
    }

    if (BASE_SOURCE_LANGUAGE_CODES.has(normalized)) {
        return normalizedCanonicalLanguageCodes.get(normalized) || normalized;
    }

    const base = normalized.split("-")[0];
    if (BASE_SOURCE_LANGUAGE_CODES.has(base)) {
        return normalizedCanonicalLanguageCodes.get(base) || base;
    }

    return canonicalizeLanguageCode(base) || base;
}

const LANGUAGE_CODE_ALIASES = new Map<string, string>([
    ["fil", "tl"],
]);

export const LEGACY_CLONING_LOCALE_ALIASES = new Map<string, string>([
    ["en", "en-US"],
    ["es", "es-MX"],
    ["pt", "pt-BR"],
    ["fr", "fr-FR"],
]);

const normalizedCanonicalLanguageCodes = new Map<string, string>();

[...youtubeLanguages, ...languages].forEach((language) => {
    const normalizedCode = normalizeLanguageCodeForRouting(language.code);
    if (!normalizedCanonicalLanguageCodes.has(normalizedCode)) {
        normalizedCanonicalLanguageCodes.set(normalizedCode, language.code);
    }
});

LANGUAGE_CODE_ALIASES.forEach((canonicalCode, aliasCode) => {
    normalizedCanonicalLanguageCodes.set(
        normalizeLanguageCodeForRouting(aliasCode),
        canonicalCode,
    );
});

export function canonicalizeLanguageCode(code: unknown): string {
    const normalizedCode = normalizeLanguageCodeForRouting(code);
    if (!normalizedCode) return "";
    return normalizedCanonicalLanguageCodes.get(normalizedCode) || normalizedCode;
}

export function normalizeCloningLocaleAlias(code: unknown): string {
    const canonicalCode = canonicalizeLanguageCode(code);
    const normalizedCode = normalizeLanguageCodeForRouting(canonicalCode);
    return LEGACY_CLONING_LOCALE_ALIASES.get(normalizedCode) || canonicalCode;
}

export function getBaseLanguageCodeForRouting(code: unknown): string {
    return normalizeLanguageCodeForRouting(canonicalizeLanguageCode(code)).split("-")[0];
}

function getUniqueLanguageOptions(languageOptions: LanguageInfo[]): { code: string, label: string }[] {
    const uniqueLanguageOptions = new Map<string, { code: string, label: string }>();

    languageOptions.forEach((language) => {
        const normalizedCode = normalizeLanguageCodeForRouting(language.code);
        if (!uniqueLanguageOptions.has(normalizedCode)) {
            uniqueLanguageOptions.set(normalizedCode, {code: language.code, label: language.label});
        }
    });

    return Array.from(uniqueLanguageOptions.values())
        .sort((a, b) => a.label.localeCompare(b.label));
}

// Source STT/import: base language codes only — no regional variants (es-419, en-GB, etc.).
// Regional labels like "Spanish (Latin America)" live in youtubeLanguages for YouTube UI only.
export function getSourceLanguages(): { code: string, label: string }[] {
    return getUniqueLanguageOptions(
        languages.filter((language) => !language.code.includes("-")),
    );
}

// Auto Dubbing (ElevenLabs) support — initially mirror the previous playht list
// and control Auto Dubbing gating with this list.
const ELEVENLABS_SUPPORTED_CODES = new Set<string>([
    // Mapped to our LanguageUtils codes (e.g., Filipino -> tl)
    "en", // English
    "hi", // Hindi
    "pt", // Portuguese
    "zh", // Chinese (Mandarin umbrella)
    "es", // Spanish
    "fr", // French
    "de", // German
    "ja", // Japanese
    "ar", // Arabic
    "ru", // Russian
    "ko", // Korean
    "id", // Indonesian
    "it", // Italian
    "nl", // Dutch
    "tr", // Turkish
    "pl", // Polish
    "sv", // Swedish
    "tl", // Filipino (provider lists fil)
    "ms", // Malay
    "ro", // Romanian
    "uk", // Ukrainian
    "el", // Greek
    "cs", // Czech
    "da", // Danish
    "fi", // Finnish
    "bg", // Bulgarian
    "hr", // Croatian
    "sk", // Slovak
    "ta", // Tamil
].map(normalizeLanguageCodeForRouting));

export function isElevenLabsSupported(code: unknown): boolean {
    return ELEVENLABS_SUPPORTED_CODES.has(normalizeLanguageCodeForRouting(code));
}

const STANDARD_DUBBING_SUPPORTED_CODES = new Set<string>([
    ...Array.from(ELEVENLABS_SUPPORTED_CODES),
]);

export function isStandardDubbingSupported(code: unknown): boolean {
    return STANDARD_DUBBING_SUPPORTED_CODES.has(normalizeLanguageCodeForRouting(code));
}

const CAPTIONS_ONLY_SUPPORTED_CODES = new Set<string>([
    ...Array.from(STANDARD_DUBBING_SUPPORTED_CODES),
    normalizeLanguageCodeForRouting("lg"), // Luganda
    normalizeLanguageCodeForRouting("ny"), // Chichewa / Chewa
]);

// Braiv Voice Cloning (TTS) supported languages (superset)
const CLONING_PREMIUM_SUPPORTED_CODES = new Set<string>([
    // Core set provided by TTS vendor
    "en-AU","en-GB","en-US",
    "es-ES","es-MX",
    "pt-BR","pt-PT",
    "fr-FR","fr-CA",
    "de","zh","ja","hi",
    "it","ko","nl","pl",
    "ru","sv","tr","tl",
    "bg","ro","ar","cs",
    "el","fi","hr","ms",
    "sk","da","ta","uk",
    "hu","no","vi","bn",
    "th","he","ka","id",
    "te","gu","kn","ml",
    "mr","pa",
].map(normalizeLanguageCodeForRouting));

const CLONING_PLUS_LANGUAGE_CODES = new Set<string>([
    ...youtubeLanguages
        .map((language) => normalizeLanguageCodeForRouting(language.code))
        .filter((code) => !code.includes("-"))
        .filter((code) => !CLONING_PREMIUM_SUPPORTED_CODES.has(code)),
    normalizeLanguageCodeForRouting("lg"), // Luganda
    normalizeLanguageCodeForRouting("ny"), // Chichewa / Chewa
]);

const BRAIV_PLUS_SUPPORTED_CODES = new Set<string>([
    ...Array.from(normalizedCanonicalLanguageCodes.keys()),
    ...Array.from(CLONING_PLUS_LANGUAGE_CODES),
]);

export function isCloningSupported(code: unknown): boolean {
    return CLONING_PREMIUM_SUPPORTED_CODES.has(normalizeLanguageCodeForRouting(code));
}

export function isBraivPlusSupported(code: unknown): boolean {
    return BRAIV_PLUS_SUPPORTED_CODES.has(normalizeLanguageCodeForRouting(code));
}

export function getCloningLanguages(): { code: string, label: string }[] {
    return getUniqueLanguageOptions([...languages, ...youtubeLanguages])
        .filter((language) => getCloningLanguageTier(language.code) !== null);
}

export function getCloningLanguageTier(code: unknown): "premium" | "plus" | null {
    const normalizedCode = normalizeLanguageCodeForRouting(code);
    if (!normalizedCode) return null;

    if (CLONING_PLUS_LANGUAGE_CODES.has(normalizedCode)) {
        return "plus";
    }

    if (CLONING_PREMIUM_SUPPORTED_CODES.has(normalizedCode)) {
        return "premium";
    }

    return null;
}

export function resolveDubbingTypeForLanguage(code: string): RoutedDubbingType {
    if (isCloningSupported(code)) {
        return "cloning-dubbing";
    }

    if (isBraivPlusSupported(code)) {
        return "braiv-dubbing-plus";
    }

    return "standard-dubbing";
}

export function mapProviderLanguageCode(process: TranslationProcess, code: string): string {
    const canonicalCode = canonicalizeLanguageCode(code);

    // ElevenLabs expects 'fil' for Filipino; our internal code is 'tl'.
    if (process === "elevenlabs-dubbing" && canonicalCode === 'tl') return 'fil';
    return canonicalCode;
}

// Per-process language lists — Phase 1: keep current behavior (broad) except Auto Dubbing.
export function getLanguagesForProcess(process: TranslationProcess): { code: string, label: string }[] {
    if (process === "elevenlabs-dubbing") {
        return languages
            .filter((l) => isElevenLabsSupported(l.code))
            .map((l) => ({code: l.code, label: l.label}));
    }
    if (process === "standard-dubbing") {
        return languages
            .filter((l) => isStandardDubbingSupported(l.code))
            .map((l) => ({code: l.code, label: l.label}));
    }
    if (process === "translate-captions-only") {
        return languages
            .filter((l) => CAPTIONS_ONLY_SUPPORTED_CODES.has(normalizeLanguageCodeForRouting(l.code)))
            .map((l) => ({code: l.code, label: l.label}));
    }
    // For Phase 1, other processes remain unrestricted (use full list)
    return languages.map((l) => ({code: l.code, label: l.label}));
}


